import type { TutorContactRequestMessage } from '@brighterly/lib-core-types/tutor-contact/interfaces'
import type { TutorContactChatCopyContext, TutorContactChatMessage } from '../model/types/tutor-contact/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'
import { TutorContactMessageIntent, TutorContactMessageRole } from '@brighterly/lib-core-types/tutor-contact/enums'

import dayjs from 'dayjs'

import { formatBookedDate, formatTimeAmPm } from '@shared/lib/format'

export const TUTOR_CONTACT_COMMIT_TEXT = 'I\'ll be there'
export const TUTOR_CONTACT_MESSAGE_MAX_LENGTH = 1000

let messageCounter = 0

const nextMessageId = (): string => `tutor-contact-${++messageCounter}`

export const nowChatTime = (): string => dayjs().format(DateFormat.AmPm)

export const createChatMessage = (
  role: TutorContactMessageRole,
  text: string,
  time: string | null = null,
): TutorContactChatMessage => ({
  id: nextMessageId(),
  role,
  text,
  time,
})

export const buildGreetingText = ({ parentName, kidName, tutorName }: TutorContactChatCopyContext): string =>
  `Hi ${parentName} 👋 What would you like to discuss 1-1 with Tutor ${tutorName}? It’ll be arranged at ${kidName}’s next lesson.`

export const buildProposalText = (question: string): string =>
  `Got it: “${question}” Anything else you’d like to add?`

export const buildCapturedText = (
  { kidName, tutorName }: TutorContactChatCopyContext,
  question: string | null,
  nextLessonStart: string,
): string => {
  const topic = question ? `: “${question}”` : ' your question'
  const lesson = `${formatBookedDate(nextLessonStart)} at ${formatTimeAmPm(nextLessonStart)}`

  return `Got it. ${tutorName} would like to meet you 1-1 to go through${topic}. Join the last 10 minutes of ${kidName}’s next lesson on ${lesson}?`
}

export const buildCanceledText = ({ kidName }: TutorContactChatCopyContext): string =>
  `Thanks for writing. I couldn’t note a question for the tutor this time — you can always raise it during ${kidName}’s lesson.`

export const buildSeeYouText = (nextLessonStart: string): string =>
  `See you on ${formatBookedDate(nextLessonStart)}`

export const restoreChatMessages = (messages: TutorContactRequestMessage[]): TutorContactChatMessage[] =>
  messages.map(({ role, intent, content }) => createChatMessage(
    role,
    intent === TutorContactMessageIntent.QuestionProposed ? buildProposalText(content) : content,
  ))

export const findProposedQuestion = (messages: TutorContactRequestMessage[]): string | null =>
  messages
    .filter(({ intent }) => intent === TutorContactMessageIntent.QuestionProposed)
    .at(-1)?.content ?? null
